<template>
  <div class="wac-guides">
    <!-- ░░ Lanzador flotante (FAB) ░░ -->
    <transition name="wac-launch">
      <button
        v-if="!open && !fabHidden"
        class="wac-fab"
        :class="{ 'is-idle': !seen }"
        @click="openDrawer"
        :aria-label="$t('appViews.guides.openCenter')"
      >
        <span class="wac-fab__glow"></span>
        <span class="wac-fab__sheen"></span>
        <span class="wac-fab__disc">
          <span class="wac-fab__play" v-html="ic.playSm"></span>
        </span>
        <span class="wac-fab__label">{{ $t('appViews.guides.fabLabel') }}</span>
        <span v-if="pending > 0" class="wac-fab__badge">{{ pending }}</span>
      </button>
    </transition>

    <!-- ░░ Asistente contextual (popup IA) ░░ -->
    <transition name="wac-ai">
      <div
        v-if="aiVisible && aiData && !fabHidden"
        class="wac-ai"
        role="dialog"
        :aria-label="$t('appViews.guides.assistantAria')"
      >
        <button class="wac-ai__x" @click.stop="dismissAi" :aria-label="$t('appViews.guides.dontShowAgain')">
          <span v-html="ic.x"></span>
        </button>
        <button class="wac-ai__body" @click="openFromAi">
          <span class="wac-ai__orb">
            <span class="wac-ai__orbwave"></span>
            <span class="wac-ai__orbico" v-html="ic.sparkle"></span>
          </span>
          <span class="wac-ai__text">
            <span class="wac-ai__eyebrow"><i class="wac-ai__live"></i>{{ $t('appViews.guides.ai.assistant') }}</span>
            <span class="wac-ai__q">{{ aiData.q }}</span>
            <span class="wac-ai__cta">{{ aiData.cta }}<span v-html="ic.arrow"></span></span>
          </span>
        </button>
      </div>
    </transition>

    <!-- ░░ Scrim + panel (navegador de guías) ░░ -->
    <transition name="wac-fade">
      <div v-if="open" class="wac-scrim" @click="close"></div>
    </transition>

    <transition name="wac-drawer">
      <aside v-if="open" class="wac-panel" role="dialog" :aria-label="$t('appViews.guides.centerAria')">
        <header class="wac-head">
          <div class="wac-head__top">
            <div class="wac-head__title">
              <span class="wac-head__eyebrow">{{ $t('appViews.guides.eyebrow') }}</span>
              <h2>{{ $t('appViews.guides.title') }}</h2>
            </div>
            <div class="wac-ring" :title="$t('appViews.guides.watchedOf', { watched: watchedCount, total: total })">
              <svg viewBox="0 0 44 44">
                <circle class="wac-ring__bg" cx="22" cy="22" r="18" />
                <circle
                  class="wac-ring__fg"
                  cx="22" cy="22" r="18"
                  :stroke-dasharray="ringLen"
                  :stroke-dashoffset="ringLen * (1 - progress)"
                />
              </svg>
              <span class="wac-ring__num">{{ watchedCount }}<i>/{{ total }}</i></span>
            </div>
            <button class="wac-iconbtn" @click="close" :aria-label="$t('appViews.guides.close')">
              <span v-html="ic.x"></span>
            </button>
          </div>

          <div class="wac-search">
            <span class="wac-search__i" v-html="ic.search"></span>
            <input
              v-model="query"
              type="text"
              :placeholder="$t('appViews.guides.searchPlaceholder')"
              @keydown.esc.stop="query ? (query = '') : close()"
            />
            <button v-if="query" class="wac-search__clear" @click="query = ''" v-html="ic.x"></button>
          </div>
        </header>

        <div class="wac-body">
          <div
            v-for="section in visibleSections"
            :key="section.key"
            :ref="'sec-' + section.key"
            class="wac-sec"
            :class="{ 'is-focus': section.key === highlightKey }"
          >
            <div class="wac-sec__head">
              <span class="wac-sec__chip">
                <span
                  class="wac-sec__ico"
                  :style="{ background: section.color[0], color: section.color[1] }"
                  v-html="ic[section.icon]"
                ></span>
                <span class="wac-sec__label">{{ section.label }}</span>
              </span>
              <span
                v-if="!query && section.key === routeKey"
                class="wac-sec__here"
              >
                <span v-html="ic.pin"></span> {{ $t('appViews.guides.forThisScreen') }}
              </span>
              <span v-else-if="section.badge" class="wac-sec__badge">{{ section.badge }}</span>
              <span class="wac-sec__line"></span>
            </div>

            <button
              v-for="v in section.videos"
              :key="v.id"
              class="wac-card"
              :class="{ 'is-active': v.id === activeId, 'is-done': isWatched(v.id) }"
              @click="play(v)"
            >
              <span class="wac-card__thumb">
                <img :src="thumb(v.id)" loading="lazy" alt="" />
                <span class="wac-card__scrim"></span>
                <span class="wac-card__play" v-html="ic.playSm"></span>
                <span class="wac-card__num">{{ pad(v._i + 1) }}</span>
              </span>
              <span class="wac-card__text">
                <span class="wac-card__t">{{ v.title }}</span>
                <span class="wac-card__meta">
                  <template v-if="isWatched(v.id)"><i class="dot done"></i> {{ $t('appViews.guides.watched') }}</template>
                  <template v-else><i class="dot"></i> {{ $t('appViews.guides.watchGuide') }}</template>
                </span>
              </span>
              <span class="wac-card__check" :class="{ on: isWatched(v.id) }" v-html="ic.check"></span>
            </button>

            <div v-if="!section.videos.length" class="wac-sec__soon">
              <span class="wac-sec__soonico" v-html="ic.rocket"></span>
              <p>{{ $t('appViews.guides.comingSoon') }}</p>
            </div>
          </div>

          <div v-if="visibleSections.length === 0" class="wac-empty">
            <span v-html="ic.search"></span>
            <p>{{ $t('appViews.guides.noResults', { query: query }) }}</p>
          </div>

          <footer class="wac-foot">
            <span v-html="ic.help"></span>
            {{ $t('appViews.guides.needHelp') }}<a :href="waLink" target="_blank" rel="noopener">{{ $t('appViews.guides.contactWhatsApp') }}</a>
          </footer>
        </div>
      </aside>
    </transition>

    <!-- ░░ Lightbox del reproductor (centrado, grande) ░░ -->
    <transition name="wac-light">
      <div v-if="activeVideo" class="wac-light" @click.self="closePlayer">
        <div class="wac-light__card" role="dialog" :aria-label="$t('appViews.guides.playerAria')">
          <div class="wac-light__head">
            <div class="wac-light__meta">
              <span class="wac-light__eyebrow">{{ $t('appViews.guides.lightboxMeta', { section: activeVideo._section, n: activeVideo._i + 1, total: total }) }}</span>
              <h3 class="wac-light__title">{{ activeVideo.title }}</h3>
            </div>
            <button class="wac-light__x" @click="closePlayer" :aria-label="$t('appViews.guides.close')">
              <span v-html="ic.x"></span>
            </button>
          </div>

          <div class="wac-light__frame">
            <iframe
              :key="activeVideo.id"
              :src="embedUrl(activeVideo.id)"
              :title="$t('appViews.guides.videoTitle')"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            ></iframe>
          </div>

          <div class="wac-light__foot">
            <button class="wac-light__nav" :disabled="!prevVideo" @click="go(-1)">
              <span v-html="ic.chevL"></span> {{ $t('appViews.guides.prev') }}
            </button>
            <a class="wac-light__yt" :href="'https://youtu.be/' + activeVideo.id" target="_blank" rel="noopener">
              {{ $t('appViews.guides.watchOnYouTube') }} <span v-html="ic.ext"></span>
            </a>
            <button class="wac-light__nav" :disabled="!nextVideo" @click="go(1)">
              {{ $t('appViews.guides.next') }} <span v-html="ic.chevR"></span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { ocultarFab, mostrarFab } from '@/utils/fabOculto';
// Centro de guías en video del panel. Orden onboarding: panel → alojamiento →
// tarifas → calendario → huésped → autoridades → novedades. Estado "visto" en
// localStorage. Abrir desde cualquier sitio con this.$root.$emit('open-video-guides').
// El asistente contextual (popup IA) reordena las guías según la pantalla actual
// y ofrece la guía relevante; si el usuario lo descarta, no vuelve a aparecer.
const WATCHED_KEY = "wac_guides_watched_v1";
const SEEN_KEY = "wac_guides_seen_v1";
const AI_OFF_KEY = "wac_guides_ai_off_v1";
const AI_SHOWN_KEY = "wac_guides_ai_shown_v1"; // secciones ya sugeridas (no repetir nunca)
const WA_LINK = "https://wa.me/34642982154"; // WhatsApp de soporte

// Calendarios a pantalla completa: el FAB (y el popup) abajo-derecha estorban —
// chocan con las barras de reservas, algunas casi del mismo color. En estas vistas
// el acceso a Guías va SOLO en la cabecera (maquetaCreate/Nav.vue).
// Pantallas a las que el FAB de Guías ESTORBA: los calendarios a ancho completo y la
// planificación de limpiezas, que tiene su propio botón flotante abajo a la derecha y
// se solapaban el uno con el otro.
const HIDE_FAB_ROUTES = ["ReservationCalendar", "monthlyCalendar", "hotelReservationCalendar", "hourlyCalendar", "availabilityPrices", "HousekeepingCalendar"];

// Capas que tapan la pantalla. Los drawers y modales del panel no comparten una clase
// común (csd-overlay, vmodal-overlay, hrm-overlay, rc-overlay, rt-edit-backdrop…), así
// que se buscan por la parte del nombre que sí comparten y luego se comprueba de verdad
// su geometría: fija, visible y cubriendo la pantalla. Sin eso, el FAB de Guías se
// quedaba flotando encima del drawer, tapando su botón de guardar.
const OVERLAY_SELECTOR = [
  '[class*="overlay"]',
  '[class*="backdrop"]',
  '[class*="scrim"]',
  '[class*="drawer"]',
  ".p-sidebar",
  ".p-dialog",
  ".modal.show",
  // Los que se anuncian como diálogo aunque su clase no diga nada («ad», el panel
  // de «Conecta tu propio dominio»): así un drawer nuevo no depende de cómo se llame.
  '[role="dialog"]',
  '[aria-modal="true"]',
].join(",");

const OVERLAY_HINT = /overlay|backdrop|scrim|drawer|sidebar|dialog|modal|panel/i;

/** ¿Este nodo que entra o sale del DOM puede ser una capa que tape la pantalla? */
function huelaACapa(n) {
  if (OVERLAY_HINT.test(String(n.className || ""))) return true;
  return typeof n.getAttribute === "function" &&
    (n.getAttribute("role") === "dialog" || n.getAttribute("aria-modal") === "true");
}

// Ruta (name/path) → sección de guía relevante para esa pantalla.
const ROUTE_SECTION = {
  Dashboard: "inicio", SetupWizard: "inicio", accountWelcome: "inicio",
  ListingManager: "alojamiento", ListingIndex: "alojamiento", ListingDetail: "alojamiento",
  ImageAccommodation: "alojamiento", welcomeAccommodation: "alojamiento", welcomeAccommodationProperty: "alojamiento",
  accommodationPolicies: "alojamiento", checkinProgramming: "alojamiento", Propiedades: "alojamiento",
  editionProperty: "alojamiento", nameProperty: "alojamiento", locationProperty: "alojamiento",
  serviceProperty: "alojamiento", capacityProperty: "alojamiento", descriptionProperty: "alojamiento",
  imageSettings: "alojamiento", roomTypes: "alojamiento", roomManagement: "alojamiento",
  Habitaciones: "alojamiento", QRPropiedad: "alojamiento",
  Rates: "tarifas", priceProperty: "tarifas", priceLabsConfig: "tarifas", Ofertas: "tarifas",
  ReservationCalendar: "calendario", monthlyCalendar: "calendario", Bookings: "calendario",
  Reservas: "calendario", ReservasID: "calendario", Housekeeping: "calendario", maintenance: "calendario",
  PageMensajes: "huesped", messageChannelPage: "huesped", GuestList: "huesped",
  AgentIAManager: "huesped", AgentIAPlayground: "huesped", ListingBotConfig: "huesped", pageManageChatBot: "huesped",
  Authorities: "autoridades", connectionChannel: "autoridades", Integrations: "autoridades",
  ChannelConnect: "autoridades", channelConnection: "autoridades",
};

// Secciones que tienen mensaje del asistente contextual (tono cercano, nunca
// publicitario). El texto vive en i18n (appViews.guides.ai.<key>); aquí solo
// la lista de claves válidas para decidir cuándo ofrecer una sugerencia.
const AI_KEYS = ["inicio", "alojamiento", "tarifas", "calendario", "huesped", "autoridades"];

export default {
  name: "VideoGuides",
  data() {
    return {
      open: false,
      query: "",
      activeId: null,
      watched: [],
      seen: false,
      waLink: WA_LINK,
      ringLen: 2 * Math.PI * 18,
      highlightKey: null,
      // true mientras haya un drawer/modal abierto (ver syncOverlayState)
      overlayOpen: false,
      bodyClassObserver: null,
      domObserver: null,
      overlayCheckPending: false,
      // Asistente contextual
      aiVisible: false,
      aiKey: null,
      aiOff: false,
      aiShownKeys: [], // secciones ya sugeridas en esta sesión (no repetir)
      aiTimer: null,
      aiHideTimer: null,
      sections: [
        { key: "inicio", label: this.$t("appViews.guides.sections.inicio"), icon: "compass", color: ["#F1ECFB", "#7C4DD1"],
          videos: [{ id: "o-WSm92o0Bw", title: this.$t("appViews.guides.videos.navigate") }] },
        { key: "alojamiento", label: this.$t("appViews.guides.sections.alojamiento"), icon: "home", color: ["#E7F6EF", "#11936A"],
          videos: [
            { id: "QWwCxZEWTbM", title: this.$t("appViews.guides.videos.propertyData") },
            { id: "UXUUvFhCP6Q", title: this.$t("appViews.guides.videos.checkinWelcome") },
          ] },
        { key: "tarifas", label: this.$t("appViews.guides.sections.tarifas"), icon: "tag", color: ["#E9F3FE", "#2C72C9"],
          videos: [{ id: "kcM9S8qtkYk", title: this.$t("appViews.guides.videos.basePrices") }] },
        { key: "calendario", label: this.$t("appViews.guides.sections.calendario"), icon: "calendar", color: ["#E6F6F6", "#11888C"],
          videos: [{ id: "GezW29E6LB0", title: this.$t("appViews.guides.videos.calendar") }] },
        { key: "huesped", label: this.$t("appViews.guides.sections.huesped"), icon: "spark", color: ["#FDEDF3", "#C13D7B"],
          videos: [
            { id: "THsYicTLJ4Q", title: this.$t("appViews.guides.videos.guestJourney") },
            { id: "heUK9_4So7o", title: this.$t("appViews.guides.videos.guestVerification") },
          ] },
        { key: "autoridades", label: this.$t("appViews.guides.sections.autoridades"), icon: "shield", color: ["#EEF2FF", "#4F5DD0"],
          videos: [{ id: "Bk_W4yz1DuY", title: this.$t("appViews.guides.videos.authorities") }] },
        { key: "novedades", label: this.$t("appViews.guides.sections.novedades"), icon: "rocket", color: ["#FEF4E6", "#C77A12"],
          videos: [] },
      ],
      ic: {
        playSm: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.14v13.72a1 1 0 0 0 1.53.85l10.75-6.86a1 1 0 0 0 0-1.7L9.53 4.29A1 1 0 0 0 8 5.14Z"/></svg>',
        x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>',
        search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></svg>',
        check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
        ext: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6"/><path d="M20 4 10 14"/><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>',
        help: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.8.4-1.1 1-1.1 1.8"/><path d="M12 17h.01"/></svg>',
        chevL: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m15 6-6 6 6 6"/></svg>',
        chevR: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>',
        arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13"/><path d="m12 6 6 6-6 6"/></svg>',
        pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z"/><circle cx="12" cy="10" r="2.4"/></svg>',
        sparkle: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.2c.3 2.7 1 4.6 2.2 5.8s3.1 1.9 5.8 2.2c-2.7.3-4.6 1-5.8 2.2s-1.9 3.1-2.2 5.8c-.3-2.7-1-4.6-2.2-5.8S4.9 10.7 2.2 10.4c2.7-.3 4.6-1 5.8-2.2S11.7 4.9 12 2.2Z"/><path d="M19 3c.15 1.2.5 2 1.1 2.6S21.6 6.6 22.8 6.8c-1.2.15-2 .5-2.6 1.1S19.15 9.5 19 10.7c-.15-1.2-.5-2-1.1-2.6S16.4 7 15.2 6.8c1.2-.15 2-.5 2.6-1.1S18.85 4.2 19 3Z"/></svg>',
        compass: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m15.5 8.5-2 5-5 2 2-5 5-2Z"/></svg>',
        home: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>',
        tag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4 12 22l-9-9V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z"/><circle cx="7.5" cy="7.5" r="1.4" fill="currentColor"/></svg>',
        calendar: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4.5" width="18" height="17" rx="3"/><path d="M3 9h18M8 2.5v4M16 2.5v4"/></svg>',
        spark: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7L12 3Z"/><path d="M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2Z"/></svg>',
        shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6l7-3Z"/><path d="m9 12 2 2 4-4"/></svg>',
        rocket: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M5 15c-1.5 1.3-2 5-2 5s3.7-.5 5-2M14 4c3-1 6 0 6 0s1 3 0 6l-6 6-6-6 6-6Z"/><circle cx="15" cy="9" r="1.4" fill="currentColor"/></svg>',
      },
    };
  },
  computed: {
    flat() {
      let i = 0;
      const out = [];
      this.sections.forEach((s) => s.videos.forEach((v) => { v._i = i++; v._section = s.label; out.push(v); }));
      return out;
    },
    total() { return this.flat.length; },
    watchedCount() { return this.flat.filter((v) => this.watched.includes(v.id)).length; },
    progress() { return this.total ? this.watchedCount / this.total : 0; },
    pending() { return this.total - this.watchedCount; },
    activeIndex() { return this.flat.findIndex((v) => v.id === this.activeId); },
    activeVideo() { return this.activeIndex >= 0 ? this.flat[this.activeIndex] : null; },
    prevVideo() { return this.activeIndex > 0 ? this.flat[this.activeIndex - 1] : null; },
    nextVideo() { return this.activeIndex >= 0 && this.activeIndex < this.total - 1 ? this.flat[this.activeIndex + 1] : null; },
    aiData() {
      if (!this.aiKey) return null;
      return {
        q: this.$t("appViews.guides.ai." + this.aiKey),
        cta: this.$t("appViews.guides.ai.cta"),
      };
    },
    // En los calendarios a pantalla completa NO se muestran ni el FAB ni el popup.
    // Tampoco con un drawer/modal abierto: el FAB vive en z-index 999990, por encima
    // de cualquier overlay, y se quedaba flotando sobre la bandeja de mensajes o el
    // detalle de la reserva tapando sus controles.
    fabHidden() {
      return this.overlayOpen || HIDE_FAB_ROUTES.includes((this.$route || {}).name);
    },
    // Sección de guía relevante para la ruta actual (o null si no hay).
    routeKey() {
      const r = this.$route || {};
      if (r.name && ROUTE_SECTION[r.name]) return ROUTE_SECTION[r.name];
      if (Array.isArray(r.matched)) {
        for (let i = r.matched.length - 1; i >= 0; i--) {
          const n = r.matched[i] && r.matched[i].name;
          if (n && ROUTE_SECTION[n]) return ROUTE_SECTION[n];
        }
      }
      const p = (r.path || "").toLowerCase();
      if (p.includes("calendar") || p.includes("booking") || p.includes("reserv")) return "calendario";
      if (p.includes("rate") || p.includes("price")) return "tarifas";
      if (p.includes("listing") || p.includes("propert") || p.includes("accommodation")) return "alojamiento";
      if (p.includes("message") || p.includes("chat") || p.includes("agent") || p.includes("guest")) return "huesped";
      if (p.includes("authorit") || p.includes("channel") || p.includes("integration")) return "autoridades";
      return null;
    },
    // Secciones con la relevante a la ruta primero.
    orderedSections() {
      const key = this.routeKey;
      if (!key) return this.sections;
      const hit = this.sections.filter((s) => s.key === key);
      if (!hit.length) return this.sections;
      return [...hit, ...this.sections.filter((s) => s.key !== key)];
    },
    visibleSections() {
      void this.flat; // asegura numeración/sección antes de filtrar
      const q = this.query.trim().toLowerCase();
      if (!q) return this.orderedSections;
      return this.orderedSections
        .map((s) => ({ ...s, videos: s.videos.filter((v) => v.title.toLowerCase().includes(q) || s.label.toLowerCase().includes(q)) }))
        .filter((s) => s.videos.length);
    },
  },
  methods: {
    // Marcas que dejan en el <body> los overlays de la app: `p-overflow-hidden` los
    // Dialog/Sidebar de PrimeVue con bloqueo de scroll, `modal-open` los modales de
    // Bootstrap y `rsv-drawer-open` el drawer de la reserva.
    //
    // Pero la mayoría de drawers y modales propios NO marcan el body: cada uno trae su
    // propia capa (csd-overlay, vmodal-overlay, hrm-overlay, rc-overlay…, una docena
    // larga y creciendo). Mantener esa lista a mano se queda corta sola, así que además
    // se mira el DOM: si hay una capa fija que cubre la pantalla, el FAB sobra.
    syncOverlayState() {
      const cl = document.body.classList;
      this.overlayOpen =
        cl.contains("p-overflow-hidden") ||
        cl.contains("modal-open") ||
        cl.contains("rsv-drawer-open") ||
        this.hasFullscreenOverlay();
    },

    /** ¿Hay una capa fija cubriendo la pantalla (drawer lateral, modal, panel)? */
    hasFullscreenOverlay() {
      let nodes;
      try {
        nodes = document.querySelectorAll(OVERLAY_SELECTOR);
      } catch (e) {
        return false;
      }

      const anchoMin = window.innerWidth * 0.55;
      const altoMin = window.innerHeight * 0.55;

      for (let i = 0; i < nodes.length; i++) {
        const el = nodes[i];
        // El propio centro de guías no cuenta como overlay ajeno.
        if (el.closest(".wac-guides")) continue;

        // Capas que se están cerrando: Vue mantiene el nodo con las clases de salida
        // hasta retirarlo. Si contaran, el FAB no volvería cuando la animación se
        // queda a medias (pestaña en segundo plano, movimiento reducido).
        if (/(^|\s)[\w-]*-leave(-active|-to)?(\s|$)/.test(String(el.className))) continue;

        const cs = window.getComputedStyle(el);
        if (cs.display === "none" || cs.visibility === "hidden") continue;

        /* La capa no tiene por qué ser `fixed` ELLA: el drawer de configuración de
           anuncios —y cualquiera montado igual— pone un contenedor fijo a pantalla
           completa y dentro coloca el velo y el panel en `absolute`. Exigiendo `fixed`
           al propio elemento, esos drawers no se detectaban y el botón de guías se
           quedaba encima tapando su «Guardar». */
        if (cs.position !== "fixed" && !this.dentroDeUnaCapaFija(el)) continue;
        // La opacidad NO decide: el overlay entra en 0 mientras corre su animación de
        // apertura, que es justo el instante en el que llega este aviso. Mirarla dejaba
        // el FAB encima del drawer para siempre, porque al terminar la animación ya no
        // hay ninguna mutación que vuelva a disparar la comprobación.

        const r = el.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) continue;   // aún sin pintar, o ya retirado
        // Un drawer lateral no llega al 55% de ancho: basta con que cubra el alto y
        // arranque pegado a un borde, que es lo que le pone el FAB encima.
        const cubreAlto = r.height >= altoMin;
        const cubreAncho = r.width >= anchoMin;
        const pegadoAlBorde = r.right >= window.innerWidth - 2 || r.left <= 2;

        if (cubreAlto && (cubreAncho || pegadoAlBorde)) return true;
      }

      return false;
    },

    /**
     * ¿Este elemento cuelga de un contenedor FIJO que cubre la pantalla?
     *
     * Es el patrón de los drawers del panel: un `position: fixed` a pantalla completa
     * —muchas veces con `pointer-events: none`— y dentro el velo y el panel en
     * `absolute`. Para lo que nos importa —¿hay algo tapando la pantalla?— cuenta igual
     * que si fueran fijos ellos mismos.
     */
    dentroDeUnaCapaFija(el) {
      let padre = el.parentElement;

      while (padre && padre !== document.body) {
        const cs = window.getComputedStyle(padre);

        if (cs.position === "fixed") {
          const r = padre.getBoundingClientRect();
          return r.width >= window.innerWidth * 0.9 && r.height >= window.innerHeight * 0.9;
        }

        padre = padre.parentElement;
      }

      return false;
    },

    /**
     * Agrupa varias mutaciones seguidas en una sola comprobación. Con setTimeout y no
     * con requestAnimationFrame: rAF no corre en pestañas de fondo, y ahí el FAB se
     * quedaría marcado como visible hasta que alguien volviera a la pestaña.
     */
    scheduleOverlayCheck() {
      if (this.overlayCheckPending) return;
      this.overlayCheckPending = true;
      window.setTimeout(() => {
        this.overlayCheckPending = false;
        this.syncOverlayState();
        // Segunda pasada cuando la animación de apertura o cierre ya ha terminado: el
        // tamaño definitivo del panel solo se conoce entonces.
        window.setTimeout(this.syncOverlayState, 320);
      }, 0);
    },

    /**
     * Solo vuelve a mirar el DOM cuando entra o sale algo que HUELE a overlay; si no,
     * este observador se dispararía con cada repintado de la app.
     */
    onDomMutation(mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        const nodos = [m.addedNodes, m.removedNodes];
        for (let l = 0; l < nodos.length; l++) {
          for (let j = 0; j < nodos[l].length; j++) {
            const n = nodos[l][j];
            if (n.nodeType !== 1) continue;
            if (huelaACapa(n)) {
              this.scheduleOverlayCheck();
              return;
            }
          }
        }
      }
    },
    pad(n) { return String(n).padStart(2, "0"); },
    thumb(id) { return "https://i.ytimg.com/vi/" + id + "/mqdefault.jpg"; },
    embedUrl(id) {
      // Dominio estándar de YouTube con origin explícito: más fiable para la
      // reproducción embebida que el dominio -nocookie (evita "vídeo no disponible").
      let origin = "";
      try { origin = "&origin=" + encodeURIComponent(window.location.origin); } catch (e) { /* noop */ }
      return "https://www.youtube.com/embed/" + id +
        "?autoplay=1&rel=0&modestbranding=1&playsinline=1&color=white&enablejsapi=1" + origin;
    },
    isWatched(id) { return this.watched.includes(id); },
    openDrawer() {
      this.hideAi();
      this.open = true;
      this.markSeen();
    },
    close() { this.open = false; this.activeId = null; this.highlightKey = null; },
    play(v) { this.activeId = v.id; this.markWatched(v.id); },
    go(dir) {
      const t = dir < 0 ? this.prevVideo : this.nextVideo;
      if (t) this.play(t);
    },
    closePlayer() { this.activeId = null; },
    markWatched(id) {
      if (this.watched.includes(id)) return;
      this.watched = [...this.watched, id];
      try { localStorage.setItem(WATCHED_KEY, JSON.stringify(this.watched)); } catch (e) { /* noop */ }
    },
    markSeen() {
      if (this.seen) return;
      this.seen = true;
      try { localStorage.setItem(SEEN_KEY, "1"); } catch (e) { /* noop */ }
    },
    // ── Asistente contextual ──
    scheduleAi() {
      this.clearAiTimers();
      if (this.aiOff || this.open || this.activeId || this.fabHidden) return;
      const key = this.routeKey || (this.watchedCount === 0 ? "inicio" : null);
      if (!key || !AI_KEYS.includes(key)) return;
      if (this.aiShownKeys.includes(key)) return;      // ya se sugirió antes (persistido)
      if (!this.sectionHasUnwatched(key)) return;      // ya vio todos los vídeos de esa sección
      this.aiTimer = setTimeout(() => {
        if (this.aiOff || this.open || this.activeId) return;
        if (!this.sectionHasUnwatched(key)) return;    // por si los vio mientras esperaba
        this.aiKey = key;
        this.aiVisible = true;
        this.rememberAiShown(key);
        this.aiHideTimer = setTimeout(() => { this.aiVisible = false; }, 13000);
      }, 6500);
    },
    // ¿Queda algo por ver en esa sección? Si ya vio todo, no tiene sentido sugerirla.
    sectionHasUnwatched(key) {
      const s = this.sections.find((x) => x.key === key);
      if (!s || !s.videos.length) return false;
      return s.videos.some((v) => !this.watched.includes(v.id));
    },
    rememberAiShown(key) {
      if (this.aiShownKeys.includes(key)) return;
      this.aiShownKeys = [...this.aiShownKeys, key];
      try { localStorage.setItem(AI_SHOWN_KEY, JSON.stringify(this.aiShownKeys)); } catch (e) { /* noop */ }
    },
    openFromAi() {
      const key = this.aiKey;
      this.hideAi();
      this.openDrawer();
      if (key) this.focusSection(key);
    },
    /*
     * Lo que el anfitrión decide sobre este globo se guarda EN EL SERVIDOR.
     *
     * En el navegador queda solo una copia, para no enseñarlo un instante
     * mientras llega la respuesta. No puede ser la memoria buena: la pantalla
     * de acceso hace `localStorage.clear()`, así que cada vez que entraba
     * volvía a salir un globo que ya había cerrado —y con razón preguntaba
     * para qué sirve la X—. Ahora le sigue también de un equipo a otro.
     */
    dismissAi() {
      this.hideAi();
      this.aiOff = true;
      try { localStorage.setItem(AI_OFF_KEY, "1"); } catch (e) { /* noop */ }
      axios.put("/dashboard/notices", { guias: { off: true } }).catch(() => {});
    },

    async leerDelServidor() {
      try {
        const { data } = await axios.get("/dashboard/notices");
        const guias = (data && data.avisos && data.avisos.guias) || null;

        if (guias && guias.off === true) {
          this.aiOff = true;
          this.hideAi();
          try { localStorage.setItem(AI_OFF_KEY, "1"); } catch (e) { /* noop */ }
        } else if (this.aiOff) {
          // Lo había cerrado en este navegador antes de que esto se guardara
          // arriba: se sube, para que no le vuelva a salir en otro sitio.
          axios.put("/dashboard/notices", { guias: { off: true } }).catch(() => {});
        }
      } catch (e) { /* si no se puede preguntar, manda la copia del navegador */ }
    },
    hideAi() {
      this.aiVisible = false;
      this.clearAiTimers();
    },
    clearAiTimers() {
      if (this.aiTimer) { clearTimeout(this.aiTimer); this.aiTimer = null; }
      if (this.aiHideTimer) { clearTimeout(this.aiHideTimer); this.aiHideTimer = null; }
    },
    focusSection(key) {
      this.query = "";
      this.highlightKey = key;
      this.$nextTick(() => {
        const ref = this.$refs["sec-" + key];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el && el.scrollIntoView) {
          try { el.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { el.scrollIntoView(); }
        }
      });
      setTimeout(() => { if (this.highlightKey === key) this.highlightKey = null; }, 1800);
    },
    onKey(e) {
      if (e.key !== "Escape") return;
      if (this.activeId) this.closePlayer();
      else if (this.open) this.close();
    },
  },
  watch: {
    open(v) { try { document.body.style.overflow = v ? "hidden" : ""; } catch (e) { /* noop */ } },
    $route() { this.scheduleAi(); this.scheduleOverlayCheck(); },
    // El v-if saca el FAB, pero su animación de salida no siempre llega a terminar
    // (pestaña en segundo plano, movimiento reducido) y el botón se queda pintado
    // sobre el drawer. Esta marca en el <body> lo esconde al instante, sin depender
    // de que la animación acabe — el mismo refuerzo que ya existía para PrimeVue.
    overlayOpen(v) {
      // Petición, no interruptor: el asistente puede estar pidiendo lo mismo y
      // no debe perder su petición porque este overlay se cierre antes.
      if (v) ocultarFab();
      else mostrarFab();
    },
  },
  mounted() {
    try {
      this.watched = JSON.parse(localStorage.getItem(WATCHED_KEY) || "[]");
      this.seen = localStorage.getItem(SEEN_KEY) === "1";
      this.aiOff = localStorage.getItem(AI_OFF_KEY) === "1";
      this.aiShownKeys = JSON.parse(localStorage.getItem(AI_SHOWN_KEY) || "[]");
    } catch (e) { /* noop */ }
    document.addEventListener("keydown", this.onKey);
    this.$root.$on("open-video-guides", this.openDrawer);
    this.leerDelServidor();
    this.scheduleAi();

    // Los overlays de la app (PrimeVue Dialog/Sidebar y los modales de Bootstrap)
    // marcan el <body> al abrirse. Vigilamos esa clase para esconder el FAB.
    this.syncOverlayState();
    this.bodyClassObserver = new MutationObserver(this.syncOverlayState);
    this.bodyClassObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Los drawers propios no marcan el <body>: se vigila su entrada y salida del DOM.
    this.domObserver = new MutationObserver(this.onDomMutation);
    this.domObserver.observe(document.body, { childList: true, subtree: true });
  },
  beforeDestroy() {
    if (this.bodyClassObserver) this.bodyClassObserver.disconnect();
    if (this.domObserver) this.domObserver.disconnect();
    // Sólo se retira lo que este componente había pedido.
    if (this.overlayOpen) mostrarFab();
    document.removeEventListener("keydown", this.onKey);
    this.$root.$off("open-video-guides", this.openDrawer);
    this.clearAiTimers();
    try { document.body.style.overflow = ""; } catch (e) { /* noop */ }
  },
};
</script>

<style lang="scss" scoped>
$purple: #673de6;
$ink: #14121c;
$sub: #6b7280;
$hair: #eceef3;
$font: "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
$eOut: cubic-bezier(0.23, 1, 0.32, 1);
$eDrawer: cubic-bezier(0.32, 0.72, 0, 1);

.wac-guides { position: fixed; z-index: 999990; inset: 0; pointer-events: none; text-align: left; font-family: $font; }

/* Refuerzo del `fabHidden`: el v-if quita el FAB, pero su transición de salida puede
   quedarse a medias (pestaña en segundo plano, animaciones limitadas) y dejarlo visible
   ENCIMA del drawer. Estas marcas las pone el <body> al abrirse cualquier overlay, así
   que el ocultado es inmediato y no depende de que la animación termine. */
body.p-overflow-hidden .wac-fab,
body.modal-open .wac-fab,
body.rsv-drawer-open .wac-fab,
body.wac-hide-fab .wac-fab,
body.p-overflow-hidden .wac-ai,
body.modal-open .wac-ai,
body.rsv-drawer-open .wac-ai,
body.wac-hide-fab .wac-ai { display: none !important; }
.wac-guides > * { pointer-events: auto; }
.wac-guides * { box-sizing: border-box; }
.wac-guides button, .wac-guides input, .wac-guides a { font-family: $font; }

/* ── Lanzador (FAB) ──
   Pestaña PEGADA al borde derecho, no una píldora flotando por encima del
   contenido. La píldora con su texto se cruzaba por delante de las tablas y de
   la paginación —y al ser ancha, estorbaba de verdad—. Ahora asoma solo el
   disco con el play; el nombre aparece al pasar por encima, como una etiqueta
   al lado, sin mover nada de sitio. */
.wac-fab {
  position: fixed; right: 0; bottom: 28px;
  display: flex; align-items: center; justify-content: center;
  width: 46px; height: 54px; padding: 0; border: 0; cursor: pointer;
  border-radius: 16px 0 0 16px; color: #fff;
  background: linear-gradient(135deg, #825bff 0%, #{$purple} 52%, #5a2fd6 100%);
  box-shadow: -10px 12px 28px -12px rgba(18, 15, 30, 0.42), 0 2px 8px rgba(18, 15, 30, 0.16);
  transition: transform 0.24s $eOut, box-shadow 0.24s $eOut;
}
/* Barrido de brillo diagonal: se recorta en su propia capa (overflow) para que
   el badge del contador NO quede cortado por el borde del botón. */
.wac-fab__sheen { position: absolute; inset: 0; z-index: 0; border-radius: 16px 0 0 16px; overflow: hidden; pointer-events: none; }
.wac-fab__sheen::before {
  content: ""; position: absolute; inset: 0;
  background: linear-gradient(115deg, transparent 30%, rgba(255, 255, 255, 0.28) 48%, transparent 62%);
  transform: translateX(-130%); transition: transform 0.6s $eOut;
}
.wac-fab:hover .wac-fab__sheen::before { transform: translateX(130%); }
.wac-fab:hover { transform: translateX(-3px); box-shadow: -14px 16px 36px -14px rgba(18, 15, 30, 0.46), 0 3px 10px rgba(18, 15, 30, 0.2); }
.wac-fab:active { transform: translateX(-1px) scale(0.97); }
.wac-fab:focus-visible { outline: 2px solid #fff; outline-offset: -4px; }
.wac-fab__disc {
  position: relative; z-index: 1;
  width: 36px; height: 36px; flex: 0 0 auto; border-radius: 50%;
  background: rgba(255, 255, 255, 0.2); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.28);
  transition: background 0.2s $eOut, transform 0.24s $eOut;
}
.wac-fab:hover .wac-fab__disc { background: rgba(255, 255, 255, 0.3); transform: scale(1.06); }
.wac-fab__play { width: 15px; height: 15px; display: flex; margin-left: 2px; }
.wac-fab__play :deep(svg) { width: 15px; height: 15px; }
/* La etiqueta no ocupa sitio: va al lado, flotando, y solo al pasar por encima.
   Se mueve con transform y opacidad, así que no empuja nada. */
.wac-fab__label {
  position: absolute; right: calc(100% + 10px); top: 50%; z-index: 1;
  padding: 6px 11px; border-radius: 9px;
  background: $ink; color: #fff;
  font-weight: 700; font-size: 12.5px; letter-spacing: -0.1px; white-space: nowrap;
  box-shadow: 0 8px 20px -8px rgba(18, 15, 30, 0.45);
  opacity: 0; transform: translate(6px, -50%); pointer-events: none;
  transition: opacity 0.18s $eOut, transform 0.18s $eOut;
}
.wac-fab:hover .wac-fab__label,
.wac-fab:focus-visible .wac-fab__label { opacity: 1; transform: translate(0, -50%); }
@media (prefers-reduced-motion: reduce) {
  .wac-fab, .wac-fab__label, .wac-fab__disc { transition: none; }
}
/* Halo de respiración cuando aún no se ha abierto nunca */
.wac-fab__glow { position: absolute; inset: 0; z-index: 0; border-radius: 16px 0 0 16px; pointer-events: none; box-shadow: 0 0 0 0 rgba(130, 91, 255, 0.5); }
.wac-fab.is-idle .wac-fab__glow { animation: wacPulse 2.6s $eOut infinite; }
@keyframes wacPulse { 0% { box-shadow: 0 0 0 0 rgba(130, 91, 255, 0.42); } 70% { box-shadow: 0 0 0 15px rgba(130, 91, 255, 0); } 100% { box-shadow: 0 0 0 0 rgba(130, 91, 255, 0); } }
/* A la izquierda: pegado al borde derecho, un contador «fuera» se saldría de la pantalla. */
.wac-fab__badge {
  position: absolute; top: -6px; left: -8px; z-index: 2; min-width: 21px; height: 21px; padding: 0 6px;
  border-radius: 11px; background: #ff5c8a; color: #fff; font-size: 11px; font-weight: 800; line-height: 1;
  display: flex; align-items: center; justify-content: center; border: 2px solid #fff;
  box-shadow: 0 2px 7px rgba(18, 15, 30, 0.28);
}

/* ── Asistente contextual (popup IA) ── */
.wac-ai {
  position: fixed; right: 14px; bottom: 92px; width: 294px;
  background: #fff; border-radius: 18px; padding: 4px;
  box-shadow: 0 24px 60px -18px rgba(18, 15, 30, 0.24), 0 2px 10px rgba(18, 15, 30, 0.06), 0 0 0 1px rgba(18, 15, 30, 0.04);
  transform-origin: bottom right;
}
.wac-ai::after { content: ""; position: absolute; right: 12px; bottom: -7px; width: 15px; height: 15px; background: #fff; transform: rotate(45deg); border-radius: 3px; box-shadow: 3px 3px 8px -3px rgba(18, 15, 30, 0.14); }
.wac-ai__x {
  position: absolute; top: 8px; right: 8px; z-index: 2; width: 24px; height: 24px; border-radius: 50%;
  border: 0; background: #f3f2f8; color: #a49fb4; cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: background 0.16s $eOut, color 0.16s $eOut, transform 0.16s $eOut;
}
.wac-ai__x:hover { background: #ece9f6; color: #6b6577; }
.wac-ai__x:active { transform: scale(0.9); }
.wac-ai__x :deep(svg) { width: 12px; height: 12px; }
.wac-ai__body { display: flex; gap: 12px; align-items: flex-start; text-align: left; width: 100%; border: 0; background: transparent; cursor: pointer; padding: 12px 12px 13px; border-radius: 15px; transition: background 0.16s $eOut; }
.wac-ai__body:hover { background: #faf9ff; }
.wac-ai__orb {
  position: relative; width: 38px; height: 38px; flex: 0 0 auto; border-radius: 50%;
  background: linear-gradient(140deg, #8f6bff 0%, #{$purple} 55%, #5a2fd6 100%);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 16px -6px rgba(18, 15, 30, 0.22);
}
.wac-ai__orbico { display: flex; color: #fff; }
.wac-ai__orbico :deep(svg) { width: 20px; height: 20px; animation: wacSpark 3.4s ease-in-out infinite; }
@keyframes wacSpark { 0%, 100% { opacity: 0.86; transform: scale(0.94) rotate(0deg); } 50% { opacity: 1; transform: scale(1.06) rotate(8deg); } }
.wac-ai__orbwave { position: absolute; inset: 0; border-radius: 50%; box-shadow: 0 0 0 0 rgba(130, 91, 255, 0.5); animation: wacPulse 2.6s $eOut infinite; }
.wac-ai__text { flex: 1; min-width: 0; padding-right: 14px; }
.wac-ai__eyebrow { display: flex; align-items: center; gap: 6px; font-family: $font; font-size: 11px; font-weight: 700; letter-spacing: 0.2px; color: #9a95ad; }
.wac-ai__live { width: 6px; height: 6px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); animation: wacLive 1.8s ease-out infinite; }
@keyframes wacLive { 0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); } 70% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); } 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); } }
.wac-ai__q { display: block; margin: 5px 0 0; font-family: $font; font-size: 14px; font-weight: 600; color: #2b2836; line-height: 1.4; letter-spacing: -0.2px; text-wrap: pretty; }
.wac-ai__cta { display: inline-flex; align-items: center; gap: 5px; margin-top: 10px; font-size: 12.5px; font-weight: 700; color: $purple; letter-spacing: -0.1px; }
.wac-ai__cta :deep(svg) { width: 14px; height: 14px; transition: transform 0.22s $eOut; }
.wac-ai__body:hover .wac-ai__cta :deep(svg) { transform: translateX(3px); }

/* ── Scrim + panel ── */
.wac-scrim { position: fixed; inset: 0; background: rgba(18, 14, 34, 0.34); backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px); }
.wac-panel {
  position: fixed; top: 0; right: 0; height: 100vh; width: 430px; max-width: 94vw;
  background: #fbfbfd; display: flex; flex-direction: column; text-align: left;
  box-shadow: -24px 0 60px -20px rgba(24, 16, 54, 0.4);
}

.wac-head { padding: 20px 18px 14px; background: #fff; border-bottom: 1px solid $hair; }
.wac-head__top { display: flex; align-items: center; gap: 12px; }
.wac-head__title { flex: 1; min-width: 0; }
.wac-head__eyebrow { display: block; font-size: 10px; font-weight: 800; letter-spacing: 1.4px; color: $purple; }
.wac-head__title h2 { margin: 3px 0 0; font-size: 20px; font-weight: 800; color: $ink; letter-spacing: -0.5px; text-align: left; }
.wac-iconbtn { width: 34px; height: 34px; border-radius: 10px; border: 0; cursor: pointer; flex: 0 0 auto; background: #f3f2f7; color: #6b6577; display: flex; align-items: center; justify-content: center; transition: background 0.16s $eOut, color 0.16s $eOut, transform 0.16s $eOut; }
.wac-iconbtn:hover { background: #ece9f6; color: $purple; }
.wac-iconbtn:active { transform: scale(0.92); }
.wac-iconbtn :deep(svg) { width: 17px; height: 17px; }

.wac-ring { position: relative; width: 44px; height: 44px; flex: 0 0 auto; }
.wac-ring svg { width: 44px; height: 44px; transform: rotate(-90deg); }
.wac-ring__bg { fill: none; stroke: #efecf9; stroke-width: 4; }
.wac-ring__fg { fill: none; stroke: $purple; stroke-width: 4; stroke-linecap: round; transition: stroke-dashoffset 0.6s $eOut; }
.wac-ring__num { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; color: $ink; }
.wac-ring__num i { color: #b7b2c6; font-style: normal; font-weight: 700; }

.wac-search { margin-top: 14px; display: flex; align-items: center; gap: 8px; background: #f4f4f7; border-radius: 12px; padding: 0 12px; height: 42px; border: 1.5px solid transparent; transition: background 0.16s $eOut, border-color 0.16s $eOut, box-shadow 0.16s $eOut; }
.wac-search:focus-within { background: #fff; border-color: rgba(103, 61, 230, 0.4); box-shadow: 0 0 0 4px rgba(103, 61, 230, 0.08); }
.wac-search__i { color: #b1b4bf; display: flex; }
.wac-search__i :deep(svg) { width: 16px; height: 16px; }
.wac-search input { flex: 1; border: 0; background: transparent; outline: 0; font-size: 14px; font-weight: 500; color: $ink; }
.wac-search__clear { border: 0; background: transparent; color: #c2c6d0; cursor: pointer; display: flex; padding: 2px; }
.wac-search__clear :deep(svg) { width: 14px; height: 14px; }

/* Lista */
.wac-body { flex: 1; overflow-y: auto; padding: 16px 16px 8px; }
.wac-sec { margin-bottom: 20px; border-radius: 14px; transition: box-shadow 0.4s $eOut, background 0.4s $eOut; }
.wac-sec.is-focus { background: rgba(103, 61, 230, 0.05); box-shadow: 0 0 0 2px rgba(103, 61, 230, 0.28); padding: 8px; margin: -8px -8px 12px; }

/* Cabecera de sección: chip gris (icono + título) + separador aparte */
.wac-sec__head { display: flex; align-items: center; gap: 10px; margin-bottom: 11px; }
.wac-sec__chip { display: inline-flex; align-items: center; gap: 8px; flex: 0 0 auto; background: #f2f3f6; border-radius: 11px; padding: 5px 12px 5px 5px; }
.wac-sec__ico { width: 26px; height: 26px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.wac-sec__ico :deep(svg) { width: 15px; height: 15px; }
.wac-sec__label { font-size: 12.5px; font-weight: 800; color: #1a1d23; letter-spacing: -0.1px; }
.wac-sec__here { display: inline-flex; align-items: center; gap: 4px; font-size: 9.5px; font-weight: 800; letter-spacing: 0.2px; color: $purple; background: #efeafe; padding: 3px 9px 3px 7px; border-radius: 999px; white-space: nowrap; text-transform: uppercase; }
.wac-sec__here :deep(svg) { width: 11px; height: 11px; }
.wac-sec__badge { font-size: 9.5px; font-weight: 800; letter-spacing: 0.4px; color: #c77a12; background: #fef4e6; padding: 3px 9px; border-radius: 999px; text-transform: uppercase; }
.wac-sec__line { flex: 1; height: 1px; min-width: 12px; background: linear-gradient(90deg, #e6e8ee, rgba(230, 232, 238, 0)); }

/* Placeholder de sección sin vídeos (Novedades) */
.wac-sec__soon { display: flex; align-items: center; gap: 11px; padding: 14px 15px; border-radius: 14px; background: #f5f5f8; }
.wac-sec__soonico { display: flex; color: #bda06a; }
.wac-sec__soonico :deep(svg) { width: 18px; height: 18px; }
.wac-sec__soon p { margin: 0; font-size: 12.5px; font-weight: 600; color: #9aa1ad; letter-spacing: -0.1px; }

.wac-card {
  width: 100%; text-align: left; display: flex; align-items: center; gap: 12px; cursor: pointer;
  background: #fff; border: 0; border-radius: 14px; padding: 9px; margin-bottom: 9px;
  box-shadow: 0 4px 18px rgba(24, 16, 54, 0.05);
  transition: transform 0.2s $eOut, box-shadow 0.2s $eOut;
}
.wac-card:hover { transform: translateY(-2px); box-shadow: 0 12px 26px -8px rgba(18, 15, 30, 0.14); }
.wac-card:active { transform: translateY(0) scale(0.99); }
.wac-card.is-active { background: #f5f5f8; box-shadow: 0 0 0 1.5px #e3e4ec, 0 6px 16px -8px rgba(18, 15, 30, 0.1); }
.wac-card.is-done { background: #fbfbfd; }
.wac-card__thumb { position: relative; width: 108px; height: 62px; flex: 0 0 auto; border-radius: 10px; overflow: hidden; background: #ece9f6; }
.wac-card__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.4s $eOut; }
.wac-card:hover .wac-card__thumb img { transform: scale(1.07); }
.wac-card__scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(13, 11, 22, 0) 40%, rgba(13, 11, 22, 0.35)); opacity: 0; transition: opacity 0.2s $eOut; }
.wac-card:hover .wac-card__scrim { opacity: 1; }
.wac-card__play { position: absolute; inset: 0; margin: auto; width: 32px; height: 32px; border-radius: 50%; background: rgba(13, 11, 22, 0.5); color: #fff; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(2px); transition: background 0.22s $eOut, transform 0.22s $eOut, opacity 0.22s $eOut; opacity: 0.9; }
.wac-card__play :deep(svg) { width: 13px; height: 13px; margin-left: 2px; }
.wac-card:hover .wac-card__play { background: $purple; transform: scale(1.14); opacity: 1; box-shadow: 0 6px 14px -4px rgba(18, 15, 30, 0.42); }
.wac-card__num { position: absolute; top: 5px; left: 5px; font-size: 10px; font-weight: 800; color: #fff; background: rgba(13, 11, 22, 0.6); padding: 1px 6px; border-radius: 6px; letter-spacing: 0.4px; }
.wac-card__text { flex: 1; min-width: 0; }
.wac-card__t { margin: 0; font-size: 13px; font-weight: 700; color: $ink; line-height: 1.32; letter-spacing: -0.1px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.wac-card__meta { display: inline-flex; align-items: center; gap: 5px; margin-top: 5px; font-size: 11px; font-weight: 700; color: #9aa1ad; }
.wac-card__meta .dot { width: 6px; height: 6px; border-radius: 50%; background: #d3d6de; }
.wac-card__meta .dot.done { background: #11936a; }
.wac-card.is-done .wac-card__meta { color: #11936a; }
.wac-card__check { width: 24px; height: 24px; border-radius: 50%; flex: 0 0 auto; display: flex; align-items: center; justify-content: center; border: 2px solid #e6e3f0; color: transparent; transition: background 0.24s $eOut, border-color 0.24s $eOut; }
.wac-card__check :deep(svg) { width: 13px; height: 13px; }
.wac-card__check.on { background: #11936a; border-color: #11936a; color: #fff; animation: wacPop 0.32s $eOut; }
@keyframes wacPop { 0% { transform: scale(0.4); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }

.wac-empty { text-align: center; color: #b7bcc7; padding: 40px 10px; }
.wac-empty :deep(svg) { width: 34px; height: 34px; opacity: 0.6; }
.wac-empty p { margin: 10px 0 0; font-size: 13px; font-weight: 600; }
.wac-foot { display: flex; align-items: center; gap: 7px; justify-content: center; padding: 14px 0 20px; font-size: 12px; color: $sub; text-align: center; }
.wac-foot :deep(svg) { width: 15px; height: 15px; color: #11b14d; }
.wac-foot a { color: $purple; font-weight: 700; text-decoration: none; }
.wac-foot a:hover { text-decoration: underline; }
.wac-body::-webkit-scrollbar { width: 8px; }
.wac-body::-webkit-scrollbar-thumb { background: #e3e0ec; border-radius: 8px; border: 2px solid #fbfbfd; }

/* ── Lightbox del reproductor ── */
.wac-light { position: fixed; inset: 0; z-index: 999996; display: flex; align-items: center; justify-content: center; padding: 24px; background: rgba(11, 8, 22, 0.74); backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px); }
.wac-light__card { width: min(960px, 100%); max-height: 92vh; display: flex; flex-direction: column; background: #14111f; border-radius: 20px; overflow: hidden; text-align: left; box-shadow: 0 40px 100px -30px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05); }
.wac-light__head { display: flex; align-items: flex-start; gap: 14px; padding: 16px 18px; }
.wac-light__meta { flex: 1; min-width: 0; }
.wac-light__eyebrow { display: block; font-size: 11px; font-weight: 800; letter-spacing: 0.6px; color: #b9a9f5; text-transform: uppercase; }
.wac-light__title { margin: 4px 0 0; font-size: 17px; font-weight: 800; color: #fff; letter-spacing: -0.3px; line-height: 1.25; }
.wac-light__x { width: 38px; height: 38px; border-radius: 11px; border: 0; cursor: pointer; flex: 0 0 auto; background: rgba(255, 255, 255, 0.08); color: #cfc9e2; display: flex; align-items: center; justify-content: center; transition: background 0.16s $eOut, color 0.16s $eOut, transform 0.16s $eOut; }
.wac-light__x:hover { background: rgba(255, 255, 255, 0.16); color: #fff; }
.wac-light__x:active { transform: scale(0.92); }
.wac-light__x :deep(svg) { width: 18px; height: 18px; }
.wac-light__frame { position: relative; width: 100%; padding-top: 56.25%; background: #000; }
.wac-light__frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.wac-light__foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 13px 16px; }
.wac-light__nav { display: inline-flex; align-items: center; gap: 6px; border: 0; cursor: pointer; background: rgba(255, 255, 255, 0.08); color: #e8e4f4; font-size: 12.5px; font-weight: 700; padding: 9px 13px; border-radius: 10px; transition: background 0.16s $eOut, transform 0.16s $eOut; }
.wac-light__nav:hover:not(:disabled) { background: rgba(255, 255, 255, 0.16); }
.wac-light__nav:active:not(:disabled) { transform: scale(0.96); }
.wac-light__nav:disabled { opacity: 0.32; cursor: default; }
.wac-light__nav :deep(svg) { width: 15px; height: 15px; }
.wac-light__yt { display: inline-flex; align-items: center; gap: 6px; color: #b9a9f5; font-size: 12.5px; font-weight: 700; text-decoration: none; white-space: nowrap; }
.wac-light__yt:hover { color: #fff; }
.wac-light__yt :deep(svg) { width: 14px; height: 14px; }

/* ── Transiciones ── */
.wac-fade-enter-active, .wac-fade-leave-active { transition: opacity 0.24s ease; }
.wac-fade-enter, .wac-fade-leave-to { opacity: 0; }

.wac-drawer-enter-active { transition: transform 0.42s $eDrawer; }
.wac-drawer-leave-active { transition: transform 0.28s cubic-bezier(0.55, 0, 1, 0.45); }
.wac-drawer-enter, .wac-drawer-leave-to { transform: translateX(102%); }

.wac-launch-enter-active { transition: transform 0.34s $eOut, opacity 0.34s $eOut; }
.wac-launch-leave-active { transition: transform 0.2s ease, opacity 0.2s; }
.wac-launch-enter, .wac-launch-leave-to { transform: translateY(16px) scale(0.86); opacity: 0; }

.wac-ai-enter-active { transition: transform 0.4s $eDrawer, opacity 0.34s $eOut; }
.wac-ai-leave-active { transition: transform 0.22s ease, opacity 0.22s; }
.wac-ai-enter, .wac-ai-leave-to { transform: translateY(12px) scale(0.9); opacity: 0; }

.wac-light-enter-active, .wac-light-leave-active { transition: opacity 0.3s ease; }
.wac-light-enter-active .wac-light__card { transition: transform 0.38s $eOut, opacity 0.38s; }
.wac-light-leave-active .wac-light__card { transition: transform 0.2s ease, opacity 0.2s; }
.wac-light-enter, .wac-light-leave-to { opacity: 0; }
.wac-light-enter .wac-light__card, .wac-light-leave-to .wac-light__card { transform: scale(0.93) translateY(12px); opacity: 0; }

@media (max-width: 560px) {
  .wac-ai { right: 14px; width: calc(100vw - 28px); max-width: 320px; }
  .wac-fab { right: 16px; bottom: 16px; }
}

@media (prefers-reduced-motion: reduce) {
  .wac-guides *, .wac-guides *::before, .wac-guides *::after { animation: none !important; transition-duration: 0.01ms !important; }
}
</style>
